import { useState, useRef, useEffect } from "react";
import { Modal } from "components/ui/Modal";
import { Mail, Check, Copy, Download, Printer, Info } from "lucide-react";
import { WhatsAppIcon } from "components/WhatsAppIcon";
import { QRCodeSVG } from "qrcode.react";
import { ELORIA_PHONE, ELORIA_WHATSAPP_NUMBER } from "constants/eloriaContact";
import { useAuth } from "context/AuthContext";
import { useAssistantIdentity } from "hooks/useAssistantIdentity";

interface ShareDialogProps {
  isOpen: boolean;
  onClose: () => void;
  propertyManagerName: string;
  buildingName?: string;
}

export function ShareDialog({
  isOpen,
  onClose,
  propertyManagerName,
  buildingName,
}: ShareDialogProps) {
  const { lang } = useAuth();
  const locale = lang === "es" ? "es" : "en";
  const tr = (es: string, en: string) => (locale === "es" ? es : en);

  const [copiedEmail, setCopiedEmail] = useState(false);
  const [copiedPhone, setCopiedPhone] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedShareText, setCopiedShareText] = useState(false);
  const [showEmailSection, setShowEmailSection] = useState(false);
  const [showQRSection, setShowQRSection] = useState(false);
  const { connectedWappPhone, assistantName, shareMailAddress } =
    useAssistantIdentity({
      enabled: isOpen,
    });
  const qrCodeRef = useRef<HTMLDivElement>(null);

  const normalizeWappNumber = (value: string | null | undefined): string => {
    if (!value) return "";
    return value.replace(/\D/g, "");
  };

  const getShareWappNumber = (): string => {
    const connectedDigits = normalizeWappNumber(connectedWappPhone);
    return connectedDigits || ELORIA_WHATSAPP_NUMBER;
  };

  const getSharePhoneDisplay = (): string => {
    const digits = getShareWappNumber();
    return digits ? `+${digits}` : ELORIA_PHONE;
  };

  const getShareEmail = (): string => shareMailAddress;

  const getDefaultMessage = () => {
    const buildingText = buildingName
      ? tr(
          ` del edificio '${buildingName}'`,
          ` of the building '${buildingName}'`,
        )
      : "";
    const managerText = tr(
      ` de la administración '${propertyManagerName}'`,
      ` from the management '${propertyManagerName}'`,
    );
    return locale === "es"
      ? `Hola, soy un vecino${buildingText}${managerText}`
      : `Hi, I'm a neighbor${buildingText}${managerText}`;
  };

  const [customMessage, setCustomMessage] = useState(getDefaultMessage());

  // Reset message when props change
  useEffect(() => {
    const buildingText = buildingName
      ? locale === "es"
        ? ` del edificio '${buildingName}'`
        : ` of the building '${buildingName}'`
      : "";
    const managerText =
      propertyManagerName !== "el equipo"
        ? locale === "es"
          ? ` de la administración '${propertyManagerName}'`
          : ` from the management '${propertyManagerName}'`
        : "";
    const defaultMessage =
      locale === "es"
        ? `Hola, soy un vecino${buildingText}${managerText}`
        : `Hi, I'm a neighbor${buildingText}${managerText}`;
    setCustomMessage(defaultMessage);
  }, [buildingName, propertyManagerName, locale]);

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(getShareEmail());
      setCopiedEmail(true);
      setTimeout(() => setCopiedEmail(false), 2000);
    } catch (err) {
      console.error("Failed to copy email:", err);
    }
  };

  const handleCopyPhone = async () => {
    try {
      await navigator.clipboard.writeText(getSharePhoneDisplay());
      setCopiedPhone(true);
      setTimeout(() => setCopiedPhone(false), 2000);
    } catch (err) {
      console.error("Failed to copy phone:", err);
    }
  };

  const getWhatsAppUrl = (message: string) => {
    const encodedMessage = encodeURIComponent(message);
    return `https://wa.me/${getShareWappNumber()}?text=${encodedMessage}`;
  };

  const getShareMessage = () => {
    const whatsappLink = getWhatsAppUrl(customMessage);
    return locale === "es"
      ? `Les queremos contar que incorporamos a ${assistantName}, el asistente virtual del equipo de ${propertyManagerName}.

A partir de ahora, pueden enviar sus consultas, reclamos o dudas del edificio directamente por este canal.

${assistantName} responde las 24 horas por WhatsApp al ${getSharePhoneDisplay()} o por mail (${getShareEmail()}). 
Cuando algo requiere nuestra intervención, nos lo deriva para que podamos atenderlo.

Pueden escribirle por WhatsApp aquí:
${whatsappLink}

Les pedimos que, siempre que sea posible, las consultas del edificio se realicen por este medio.`
      : `We want to let you know that we have added ${assistantName}, the virtual assistant from ${propertyManagerName}.

From now on, you can send your questions, complaints or doubts about the building directly through this channel.

${assistantName} replies 24/7 on WhatsApp at ${getSharePhoneDisplay()} or by email (${getShareEmail()}).
When something requires our attention, we forward it so we can handle it.

You can message them on WhatsApp here:
${whatsappLink}

We ask that, whenever possible, building inquiries are made through this channel.`;
  };

  const handleWhatsAppShare = () => {
    const shareMessage = getShareMessage();
    const encodedShareMessage = encodeURIComponent(shareMessage);
    const whatsappShareUrl = `https://wa.me/?text=${encodedShareMessage}`;
    window.open(whatsappShareUrl, "_blank", "noopener,noreferrer");
  };

  const handleCopyLink = async () => {
    try {
      const whatsappLink = getWhatsAppUrl(customMessage);
      await navigator.clipboard.writeText(whatsappLink);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  const handleCopyShareText = async () => {
    try {
      await navigator.clipboard.writeText(getShareMessage());
      setCopiedShareText(true);
      setTimeout(() => setCopiedShareText(false), 2000);
    } catch (err) {
      console.error("Failed to copy share text:", err);
    }
  };

  const handlePrintQR = () => {
    setShowEmailSection(false);
    setShowQRSection((prev) => !prev);
  };

  const handleDownloadQR = () => {
    if (!qrCodeRef.current) return;

    const svg = qrCodeRef.current.querySelector("svg");
    if (!svg) return;

    // Convert SVG to canvas, then to image
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    const svgBlob = new Blob([svgData], {
      type: "image/svg+xml;charset=utf-8",
    });
    const svgUrl = URL.createObjectURL(svgBlob);

    img.onload = () => {
      // Revoke the SVG blob URL now that the image has loaded
      URL.revokeObjectURL(svgUrl);

      // Add padding around QR code
      const padding = 20;
      canvas.width = img.width + padding * 2;
      canvas.height = img.height + padding * 2;

      if (ctx) {
        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, padding, padding);

        // Download the image
        canvas.toBlob((blob) => {
          if (blob) {
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `qr-eloria-${buildingName || "edificio"}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          }
        });
      }
    };

    img.onerror = () => {
      // Revoke the URL even if there's an error loading the image
      URL.revokeObjectURL(svgUrl);
    };

    img.src = svgUrl;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={tr(`Compartir ${assistantName}`, `Share ${assistantName}`)}
    >
      <div className="space-y-4">
        {/* Contact section */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-blue-900 mb-2 pb-2 border-b border-blue-200">
            {tr(`Contacto de ${assistantName}`, `${assistantName} contact`)}
          </h3>
          <p className="text-xs text-gray-700 mb-3">
            {tr(
              `Comparte el contacto de ${assistantName} con los residentes`,
              `Share ${assistantName}'s contact with the residents`,
            )}
          </p>
          <div className="space-y-2">
            {/* Email */}
            <div className="flex items-center justify-between bg-white rounded-md p-2.5 border border-gray-200">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <Mail className="h-4 w-4 text-gray-600 flex-shrink-0" />
                <span className="text-sm font-medium text-gray-900 truncate">
                  {getShareEmail()}
                </span>
              </div>
              <button
                onClick={handleCopyEmail}
                className="ml-2 flex-shrink-0 p-1 rounded hover:bg-gray-100 transition-colors"
                title={tr("Copiar email", "Copy email")}
              >
                {copiedEmail ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4 text-gray-500" />
                )}
              </button>
            </div>

            {/* WhatsApp */}
            <div className="flex items-center justify-between bg-white rounded-md p-2.5 border border-gray-200">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <WhatsAppIcon className="h-4 w-4 text-gray-600 flex-shrink-0" />
                <span className="text-sm font-medium text-gray-900">
                  {getSharePhoneDisplay()}
                </span>
              </div>
              <button
                onClick={handleCopyPhone}
                className="ml-2 flex-shrink-0 p-1 rounded hover:bg-gray-100 transition-colors"
                title={tr("Copiar teléfono", "Copy phone")}
              >
                {copiedPhone ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4 text-gray-500" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Sección Link de Whatsapp */}
        <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2 pb-2 border-b border-green-200">
            <h3 className="text-sm font-semibold text-green-900 flex-shrink-0">
              {tr(
                `Link para escribir a ${assistantName}`,
                `Link to message ${assistantName}`,
              )}
            </h3>
            <span
              className="flex-1 min-w-0 text-xs text-green-600/80 truncate"
              title={getWhatsAppUrl(customMessage)}
            >
              {getWhatsAppUrl(customMessage)}
            </span>
            <button
              onClick={handleCopyLink}
              className="flex-shrink-0 p-1 rounded hover:bg-green-100 transition-colors"
              title={tr("Copiar link", "Copy link")}
            >
              {copiedLink ? (
                <Check className="h-4 w-4 text-green-600" />
              ) : (
                <Copy className="h-4 w-4 text-green-700" />
              )}
            </button>
          </div>
          <p className="text-xs text-green-800 mb-3">
            {tr(
              `Este link abre un chat con ${assistantName} con un mensaje pre-cargado.`,
              `This link opens a chat with ${assistantName} with a pre-filled message.`,
            )}
          </p>

          <div className="space-y-3">
            {/* Message Editor Section */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">
                {tr(
                  "Mensaje inicial del residente:",
                  "Resident initial message:",
                )}
              </label>
              <textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-y min-h-[60px]"
                placeholder={tr(
                  "Escribe el mensaje que aparecerá pre-cargado...",
                  "Write the message that will be pre-filled...",
                )}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-2">
              <button
                onClick={handleWhatsAppShare}
                className="w-full flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-medium py-2 px-4 rounded-md transition-colors text-sm"
              >
                <WhatsAppIcon className="h-4 w-4" />
                <span>
                  {tr("Compartir por WhatsApp", "Share via WhatsApp")}
                </span>
              </button>
              <button
                onClick={() => {
                  setShowQRSection(false);
                  setShowEmailSection((prev) => !prev);
                }}
                className="w-full flex items-center justify-center gap-2 bg-[#1a73e8] hover:bg-[#1557b0] text-white font-medium py-2 px-4 rounded-md transition-colors text-sm"
              >
                <Mail className="h-4 w-4" />
                <span>{tr("Compartir por mail", "Share via email")}</span>
              </button>

              {/* Email Section - expandable, below mail button */}
              {showEmailSection && (
                <div className="pt-1">
                  <textarea
                    readOnly
                    value={getShareMessage()}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 text-gray-800 resize-none font-mono max-h-[180px] overflow-y-auto"
                    rows={6}
                  />
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <p className="text-xs text-gray-700 flex-1 min-w-0">
                      {tr(
                        "Copiá el texto y pegalo en el cuerpo del mail para compartir con los residentes.",
                        "Copy the text and paste it into the email body to share with the residents.",
                      )}
                    </p>
                    <button
                      onClick={handleCopyShareText}
                      className="flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-[#1a73e8] hover:bg-[#1557b0] text-white text-sm font-medium rounded-md transition-colors"
                    >
                      {copiedShareText ? (
                        <>
                          <Check className="h-4 w-4" />
                          <span>Copiado</span>
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4" />
                          <span>{tr("Copiar texto", "Copy text")}</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              <button
                onClick={handlePrintQR}
                className="w-full flex items-center justify-center gap-2 bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-md transition-colors text-sm"
              >
                <Printer className="h-4 w-4" />
                <span>
                  {tr("QR para cartel impreso", "QR for printed poster")}
                </span>
              </button>

              {/* QR Code Section */}
              {showQRSection && (
                <div className="pt-3">
                  <div className="flex gap-4 items-stretch">
                    <div
                      ref={qrCodeRef}
                      className="bg-white p-3 rounded-md border border-gray-200 flex-shrink-0"
                    >
                      <QRCodeSVG
                        key={customMessage}
                        value={getWhatsAppUrl(customMessage)}
                        size={200}
                        level="H"
                        includeMargin={true}
                      />
                    </div>
                    <div className="flex-1 flex flex-col justify-between min-h-0">
                      <div className="space-y-2">
                        <p className="text-xs text-gray-700">
                          {tr(
                            `Escaneando este código QR, los residentes pueden hablar con ${assistantName} por WhatsApp.`,
                            `By scanning this QR code, residents can talk to ${assistantName} on WhatsApp.`,
                          )}
                        </p>
                        <div className="flex items-start gap-1.5 bg-blue-50 border border-blue-200 rounded px-2 py-1">
                          <Info className="h-3 w-3 text-blue-600 flex-shrink-0 mt-0.5" />
                          <p className="text-[10px] text-blue-700">
                            {tr(
                              "Ideal para colocarlo en espacios comunes.",
                              "Ideal for placing in common areas.",
                            )}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={handleDownloadQR}
                        className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-md transition-colors self-start"
                      >
                        <Download className="h-3.5 w-3.5" />
                        <span>{tr("Descargar QR", "Download QR")}</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
